import { useState, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import { readFileBody, putToPresignedUrl, FileReadError } from '../../services/uploadService';

interface UploadDocumentsModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export default function UploadDocumentsModal({ onClose, onSaved }: UploadDocumentsModalProps) {
  const { token } = useAuth();
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [licenseNumber, setLicenseNumber] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [storageKey, setStorageKey] = useState('');

  const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleFileSelect = async (selectedFile: File) => {
    if (!ACCEPTED_TYPES.includes(selectedFile.type)) {
      setUploadError(t('settings.invalidFileType'));
      return;
    }
    if (selectedFile.size > MAX_SIZE) {
      setUploadError(t('settings.fileSizeExceeded'));
      return;
    }

    setFile(selectedFile);
    setUploadError('');
    await uploadFile(selectedFile);
  };

  const uploadFile = async (fileToUpload: File) => {
    if (!token) return;
    setUploading(true);
    setUploadProgress(0);

    try {
      // Read the file first, so we never mint an upload URL we can't use
      const body = await readFileBody(fileToUpload);

      // Get presigned upload URL
      const ext = fileToUpload.name.split('.').pop() || 'pdf';
      const { uploadUrl, storageKey: key } = await api.getGarageUploadUrl(
        { mediaType: 'TRADE_LICENSE', fileName: `license.${ext}`, contentType: fileToUpload.type },
        token
      );

      // Upload to S3
      await putToPresignedUrl(uploadUrl, body, fileToUpload.type, (p) => setUploadProgress(p.percentage));

      setStorageKey(key);
      setUploadProgress(100);
    } catch (err) {
      setUploadError(err instanceof FileReadError ? err.message : t('common.uploadFailed'));
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !licenseNumber.trim() || !storageKey) return;

    setSaving(true);
    setError('');
    try {
      await api.updateTradeLicense({ licenseNumber, storageKey }, token);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-modal-overlay">
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <div className="settings-modal-header">
          <h3>{t('settings.uploadReplace')}</h3>
          <button className="settings-modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="settings-toast settings-toast-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <p className="settings-form-section-title">{t('settings.tradeLicense')}</p>
          <div className="settings-form-row">
            <input
              value={licenseNumber}
              onChange={e => setLicenseNumber(e.target.value)}
              placeholder={t('settings.tradeLicenseNumber')}
            />
          </div>

          <div className="settings-upload-area">
            {!file && !uploading && (
              <div
                className="settings-upload-dropzone"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
              >
                <p>{t('settings.browseOrDrag')}</p>
                <p className="settings-upload-hint">{t('settings.maxFileSize')}</p>
              </div>
            )}

            {uploading && (
              <div className="settings-upload-progress">
                <p>{t('settings.uploading')} {uploadProgress}%</p>
                <div className="progress-bar"><div className="progress-fill" style={{ width: `${uploadProgress}%` }} /></div>
              </div>
            )}

            {file && !uploading && storageKey && (
              <div className="settings-upload-success">
                <p>{file.name} ✓</p>
              </div>
            )}

            {uploadError && (
              <div className="settings-upload-error">
                <p>{uploadError}</p>
                <button type="button" onClick={() => { setUploadError(''); setFile(null); fileInputRef.current?.click(); }}>
                  {t('common.retry')}
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); e.target.value = ''; }}
            />
          </div>

          <button type="submit" className="settings-btn-primary" disabled={saving || !storageKey || !licenseNumber.trim()}>
            {saving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </form>
      </div>
    </div>
  );
}
